import * as React from 'react';
import type { NationalNumber } from 'libphonenumber-js';

import { Form, FormField } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Input from 'components/inputs/input/Input';
import { ModalFrameFooter, ModalFrameInner } from 'components/modal';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { fetchCountryCodes } from 'modules/locations/reducers/worldLocation';

import { __t, applyTranslations } from 'utils/language';
import { isNotEmpty, isPhoneNumber, isRequired } from 'utils/validators';
import { getPhone, sortAndUniqueItems } from 'utils/helper/common';

import './shipping-step-phone-form.scss';

applyTranslations(require('./shipping-step-phone-form.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

import type {
  ActionButtonColor,
  Data,
  MixedSelectDropdownItem
} from 'types';

export type Phone = {
  number: NationalNumber,
  countryCode: string,
  originalNumber: string,
  country: string | undefined
}

interface Props {
  title?: string,
  minHeight?: number,
  submitButtonColor?: ActionButtonColor,
  phone: string,
  onFinish?: () => void,
  onChangePhone: (phone: string) => void,
  onCancel?: () => void,
  onBack?: () => void
}

type State = {
  phone: Phone,
  countries: Array<MixedSelectDropdownItem>
};

class ShippingStepPhoneForm extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const { phone } = props;

    this.state = {
      phone: getPhone(phone),
      countries: []
    };
  }

  componentDidMount() {
    const { phone } = this.state;

    fetchCountryCodes(phone.country).then(countries => {
      this.setState(prevState => ({ ...prevState, countries }));
    });
  }

  handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const input = event.target;

    // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/26635
    this.setState((prevState) => ({ ...prevState,
      phone: {
        ...prevState.phone,
        number: input.value
      }
    }));
  };

  handleSave = () => {
    const { onFinish, onChangePhone } = this.props;
    const { phone } = this.state;

    onChangePhone(`${phone.countryCode} ${phone.number}`);

    if (onFinish) {
      onFinish();
    }
  };

  handleCountryCodeChange = (
    items: Array<MixedSelectDropdownItem>,
    data: Data | null | undefined,
    selectedCountry: Partial<MixedSelectDropdownItem> | null | undefined
  ) => {
    this.setState((prevState) => ({
      ...prevState,
      countries: [...items],
      phone: {
        ...prevState.phone,
        countryCode: selectedCountry?.value || ''
      }
    }));
  };

  renderPhoneNumberFields(): React.ReactNode {
    const { title } = this.props;
    const { phone, countries } = this.state;
    const { number, countryCode } = phone;

    return (
      <React.Fragment>
        <div className="shipping-step-phone-form__subtitle">
          {title}
        </div>
        <div className="shipping-step-phone-form__form">
          <FormField
            label={__t('shipping_step_phone_form.address_phone_label')}
            direction="column"
            showValidationIcon={false}
            showRequiredMark
            labelType="highlighted"
            fontSize="small"
            validators={[
              isNotEmpty(number),
              isRequired(number),
              isPhoneNumber(
                `${countryCode}${number}`,
                __t('shipping_step_phone_form.phone_validation_error')
              )
            ]}
          >
            <div className="shipping-phone__container">
              <MixedSelectDropdown
                name="shipping-phone-number"
                items={sortAndUniqueItems(countries)}
                onItemsToggle={this.handleCountryCodeChange}
                withSearchable
                className="shipping-phone__country-select"
              />
              <Input
                type="text"
                name="phone"
                width="responsive"
                value={number as string}
                autoFocus={number.length === 0}
                onChange={this.handleInputChange}
                className="shipping-phone__phone-input"
              />
            </div>
          </FormField>
        </div>
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const {
      minHeight,
      submitButtonColor,
      onCancel,
      onBack
    } = this.props;

    return (
      <Form
        onSubmit={this.handleSave}
      >
        <ModalFrameInner
          minHeight={minHeight}
          narrowContent
        >
          {this.renderPhoneNumberFields()}
        </ModalFrameInner>
        <ModalFrameFooter>
          <ActionButton
            color="tertiary"
            onClick={onBack || onCancel}
          >
            {onBack ? __t('collabary_common.action_button.back') : __t('collabary_common.action_button.cancel')}
          </ActionButton>
          <ActionButton
            type="submit"
            color={submitButtonColor}
          >
            {__t('collabary_common.action_button.continue')}
          </ActionButton>
        </ModalFrameFooter>
      </Form>
    );
  }
}

export default ShippingStepPhoneForm;
